import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as processingFeeActions from '@redux/modules/processingFee'
import type { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/processingFee'
import type { ProcessingFeeModel, ProcessingFeeRequestOptions } from '@models/types'

const generateUrls = () => ({
  agencyProcessingFeesIndexUrl: '#/systemManager/processingFees?debtor_type=Agency',
  brandProcessingFeesIndexUrl: '#/systemManager/processingFees?debtor_type=Brand',
  providerProcessingFeesIndexUrl: '#/systemManager/processingFees?debtor_type=Provider',
  auditLogProcessingFeesUrl: '#/systemManager/processingFees/auditLog',
  processingFeesIndexUrl: '#/systemManager/processingFees',
})

type CreateOrUpdateProcessingFeeParams = {
  dispatch: AppDispatch,
  processingFee: ProcessingFeeModel,
  processingFeeParams: Partial<ProcessingFeeModel>,
  requestOptions?: ProcessingFeeRequestOptions,
}

const createOrUpdateProcessingFee = (params: CreateOrUpdateProcessingFeeParams) => {
  const {
    dispatch, processingFee, processingFeeParams, requestOptions,
  } = params
  const { createOrUpdateProcessingFee: updateFn } = processingFeeActions

  const updatedParams = {
    billable_id: processingFee.billable_id,
    billable_type: processingFee.billable_type,
    debtor_id: processingFee.debtor_id,
    debtor_type: processingFee.debtor_type,
    ...processingFeeParams,
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteProcessingFeePayload = {
  processingFee: ProcessingFeeModel,
  dispatch: AppDispatch,
}

const deleteProcessingFee = (params: DeleteProcessingFeePayload) => {
  const { processingFee, dispatch } = params
  const { deleteProcessingFee: deleteFn } = processingFeeActions

  return dispatch(deleteFn(processingFee))
}

type Debtor = { debtorId: number, debtorType: string }

type BulkDeleteProcessingFeesPayload = {
  debtor: Debtor,
  dispatch: AppDispatch,
}

const bulkDeleteProcessingFees = (params: BulkDeleteProcessingFeesPayload) => {
  const { debtor, dispatch } = params
  const { debtorId, debtorType } = debtor || {}

  const { bulkDeleteProcessingFees: bulkDeleteFn } = processingFeeActions

  const options = {
    debtor_id: debtorId,
    debtor_type: debtorType,
  }

  return dispatch(bulkDeleteFn(options))
}

export type ShowBulkDeleteProcessingFeesModalPayload = {
  debtor?: Debtor,
}

type BulkDeleteForDebtorPayload = {
  debtor?: Debtor,
  showBulkDeleteProcessingFeesModal: (payload: ShowBulkDeleteProcessingFeesModalPayload) => void,
}

const bulkDeleteForDebtor = (params: BulkDeleteForDebtorPayload) => new Promise((resolve, reject) => {
  const { debtor, showBulkDeleteProcessingFeesModal } = params

  if (showBulkDeleteProcessingFeesModal){
    const payload = {
      debtor,
    }

    showBulkDeleteProcessingFeesModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showBulkDeleteProcessingFeesModal not defined in PageContext callbacks'))
})

export type ShowCreateOrEditProcessingFeeModalPayload = {
  processingFee: Partial<ProcessingFeeModel>,
}

type CreateOrEditProcessingFeePayload = {
  processingFee: ProcessingFeeModel,
  showCreateOrEditProcessingFeeModal: (payload: ShowCreateOrEditProcessingFeeModalPayload) => void,
}

const createOrEditProcessingFee = (params: CreateOrEditProcessingFeePayload) => new Promise((resolve, reject) => {
  const { processingFee, showCreateOrEditProcessingFeeModal } = params

  if (showCreateOrEditProcessingFeeModal){
    const payload = {
      processingFee,
    }

    showCreateOrEditProcessingFeeModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditProcessingFeeModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProcessingFeeForm(
  processingFee: Partial<ProcessingFeeModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormPayload {
  const { customRequiredFields = [], validateOn } = options

  const processingFeeForm = useForm(
    defaultFormState,
    { entity: processingFee, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [processingFee.id, processingFee.cache_key],
  )

  return processingFeeForm
}

export function useRelations(processingFee: ProcessingFeeModel) {
  const { billable_id, billable_type } = processingFee || {}

  const { services, serviceProducts } = useSelector(reduxState => reduxState.entities)

  const serviceProduct = billable_type === 'ServiceProduct' ? serviceProducts[billable_id] : undefined
  const hasServiceProduct = billable_type === 'ServiceProduct' && serviceProduct?.service_id
  const service = hasServiceProduct ? services[serviceProduct?.service_id] : services[billable_id]

  return {
    service,
    serviceProduct,
  }
}

function useProcessingFee(initEntity: Partial<ProcessingFeeModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: processingFee } = useLatestEntity(initEntity, 'processingFees')
  const { fee_type, value } = processingFee

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const {
    showBulkDeleteProcessingFeesModal,
    showCreateOrEditProcessingFeeModal,
  } = callbacks || {}

  const { creating, deleting, loading } = useSelector(reduxState => reduxState.processingFees)

  // @ts-expect-error TS2345
  const { service, serviceProduct } = useRelations(processingFee)

  const valueText = fee_type === 'fixed' ? `$${value}` : `${value}%`

  return {
    callbacks: {
      bulkDeleteForDebtor: (debtor: Debtor) => bulkDeleteForDebtor({
        debtor, showBulkDeleteProcessingFeesModal,
      }),
      bulkDeleteProcessingFees: (debtor: Debtor) => (
        bulkDeleteProcessingFees({ debtor, dispatch })
      ),
      createOrUpdateProcessingFee: (
        processingFeeParams: Partial<ProcessingFeeModel>,
        requestOptions?: ProcessingFeeRequestOptions,
      ) => (
        createOrUpdateProcessingFee({
          // @ts-expect-error TS2322
          processingFee, processingFeeParams, dispatch, requestOptions,
        })
      ),
      // @ts-expect-error TS2322
      createOrEditProcessingFee: () => createOrEditProcessingFee({ processingFee, showCreateOrEditProcessingFeeModal }),
      // @ts-expect-error TS2322
      deleteProcessingFee: () => deleteProcessingFee({ processingFee, dispatch }),
    },
    creating,
    deleting,
    processingFee,
    loading,
    service,
    serviceProduct,
    urls: generateUrls(),
    valueText,
  }
}

export default useProcessingFee
